import React, { useEffect, useState } from "react";
// This import of ReactDom is necessary for component to function, even if it isn"t being directly used!
// eslint-disable-next-line
import * as ReactDOM from "react-dom";
import MarkdownIt from "markdown-it";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import {
  Box,
  useTheme,
  Typography,
  Modal,
  Backdrop,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Link,
} from "@mui/material";
import { BCPAngleDown, BCPXmark } from "../icons/bcp/BCPIcons";
import { useTranslation } from "react-i18next";

// Initialize markdown parser
const mdParser = new MarkdownIt();

function HelperModal(props) {
  const { open, setOpen, screenWidth } = props;

  function createData(input, output) {
    return { input, output };
  }
  const headerRows = [
    createData("# Heading level 1", "Heading Level 1"),
    createData("## Heading level 2", "Heading Level 2"),
    createData("### Heading level 3", "Heading Level 3"),
    createData("#### Heading level 4", "Heading Level 4"),
    createData("##### Heading level 5", "Heading Level 5"),
    createData("###### Heading level 6", "Heading Level 6"),
  ];

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        p={{ md: 4, xs: 2 }}
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translate(-50%, 0%)",
          maxHeight: "95vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 24,
          width: screenWidth > 900 ? "60vw" : "85vw",
          top: "65px",
          height: "calc(100vh - 80px)",

          "& .modal-icon": {
            fontSize: "50px !important",
          },

          "& .jss9": {
            fontSize: "1rem",
          },
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ textTransform: "uppercase" }}
            >
              Text Editor Functions
            </Typography>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{
              p: 1,
              overflowX: "hidden",
              m: 0,
            }}
          >
            {/* Header */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body1" gutterBottom>
                    {`Below are some explanations, examples, and tips on how to use the various tools in the text editor.`}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {`Writing out components manually will have the same result as clicking on the toolbar buttons.`}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {`In general, it is best practice to ensure that there is an empty line between each section or component of your text. For example, for best results, always put a blank line before and after any headers, lists, line-breaks, and tables.`}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-header modal-span"
                      title="Header"
                    >
                      <i class="rmel-iconfont rmel-icon-font-size modal-icon"></i>
                    </span>{" "}
                    - Header
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>

                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To create a header, add any number of signs (#) from 1 to 6 in front of a word or phrase. The number signs you use should correspond to the heading level. The more # signs, the smaller the text will be.`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Examples
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {headerRows.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body1">
                                    {row.input}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {index !== 5 ? (
                                    <Typography
                                      variant={`h${index + 1}`}
                                      component="p"
                                    >
                                      {row.output}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" component="p">
                                      {row.output}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a space between the number of signs and the
                          heading name.
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a blank line before and after a heading.
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {`Heading level 6 is smaller than normal text and can be useful for captions or disclaimers`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Bold */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-bold modal-span"
                      title="Bold"
                    >
                      <i class="rmel-iconfont rmel-icon-bold modal-icon"></i>
                    </span>{" "}
                    - Bold
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To bold text, add two asterisks before and after a word or phrase. To bold the middle of a word for emphasis, add two asterisks without spaces around the letters.`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid item xs={12}>
                      <Typography variant="h4" fontWeight="bold">
                        Examples
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography variant="h4">Input</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="h4">Output</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Typography variant="body1">
                                I just love **bold text**.
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                I just love{" "}
                                <Typography
                                  fontWeight="bold"
                                  display={"inline"}
                                >
                                  bold text
                                </Typography>
                                .
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell>
                              <Typography variant="body1">
                                **Bold text** is so cool!
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                <Typography
                                  fontWeight="bold"
                                  display={"inline"}
                                >
                                  Bold text{" "}
                                </Typography>
                                is so cool!
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell>
                              <Typography variant="body1">
                                Inner**bold**text
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                Inner
                                <Typography
                                  fontWeight="bold"
                                  display={"inline"}
                                >
                                  bold
                                </Typography>
                                text
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h4" fontWeight="bold" sx={{ pt: 2 }}>
                        Best Practices
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body1">
                        {`Always make sure that there are no trailing spaces between your opening and closing asterisks and your text. The asterisks should touch the beginning and end of your text.`}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ pt: 2 }}
                        gutterBottom
                      >
                        Bold and Italic
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body1">
                        {`To emphasize text with bold and italics at the same
                        time, add three asterisks or underscores before and
                        after a word or phrase. To bold and italicize the middle
                        of a word for emphasis, add three asterisks without
                        spaces around the letters.`}
                      </Typography>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Italic */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-italic modal-span"
                      title="Italic"
                    >
                      <i class="rmel-iconfont rmel-icon-italic modal-icon"></i>
                    </span>{" "}
                    - Italic
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To italicize text, add one asterisk before and after a word or phrase. To italicize the middle of a word for emphasis, add one asterisk without spaces around the letters.`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Examples
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  I just love **italic text**.
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  I just love{" "}
                                  <Typography
                                    fontStyle="italic"
                                    display={"inline"}
                                  >
                                    italic text
                                  </Typography>
                                  .
                                </Typography>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  **Italic text** is so cool!
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  <Typography
                                    fontStyle="italic"
                                    display={"inline"}
                                  >
                                    Italic text{" "}
                                  </Typography>
                                  is so cool!
                                </Typography>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  Inner**italic**text
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  Inner
                                  <Typography
                                    fontStyle="italic"
                                    display={"inline"}
                                  >
                                    italic
                                  </Typography>
                                  text
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {`Always make sure that there are no trailing spaces between your opening and closing asterisks and your text. The asterisks should touch the beginning and end of your text.`}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                          gutterBottom
                        >
                          Bold and Italic
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {`To emphasize text with bold and italics at the same
                        time, add three asterisks or underscores before and
                        after a word or phrase. To bold and italicize the middle
                        of a word for emphasis, add three asterisks without
                        spaces around the letters.`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Strikethrough */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-strikethrough modal-span"
                      title="Strikethrough"
                    >
                      <i class="rmel-iconfont rmel-icon-strikethrough modal-icon"></i>
                    </span>{" "}
                    - Strikethrough
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To strikethrough text, add two tildes (~) before and after a word or phrase. To strikethrough the middle of a word for emphasis, add two tildes without spaces around the letters.`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Examples
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  This text has been ~~struck through~~.
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  This text has been{" "}
                                  <Typography
                                    sx={{ textDecoration: "line-through" }}
                                    display={"inline"}
                                  >
                                    struck through
                                  </Typography>
                                  .
                                </Typography>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  Hello~~hi~~there
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  Hello
                                  <Typography
                                    sx={{ textDecoration: "line-through" }}
                                    display={"inline"}
                                  >
                                    hi
                                  </Typography>
                                  there
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>

                        <Grid item xs={12}>
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ pt: 2 }}
                          >
                            Best Practices
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body1">
                            {`Always make sure that there are no trailing spaces between your opening and closing tildes and your text. The tildes should touch the beginning and end of your text.`}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* List Unordered */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-list-unordered modal-span"
                      title="Unordered List"
                    >
                      <i class="rmel-iconfont rmel-icon-list-unordered modal-icon"></i>
                    </span>{" "}
                    - Unordered List
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`Unordered lists are a list of items in which each item begins with a bullet-point. To create an unordered list, start a new line, add an asterisk, space, then write your text. The asterisk will be converted into a bullet point. Every item in the list will have a bullet-point.`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Examples
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  * First item
                                </Typography>
                                <Typography variant="body1">
                                  * Second item
                                </Typography>
                                <Typography variant="body1">
                                  * Third item
                                </Typography>
                                <Typography variant="body1">
                                  * Fourth item
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <ul class="jss9">
                                  <li>First item</li>
                                  <li>Second item</li>
                                  <li>Third item</li>
                                  <li>Fourth item</li>
                                </ul>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a new line before the beginning of your
                          list and after the end of your list.
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Ordered List */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-list-ordered modal-span"
                      title="Ordered List"
                    >
                      <i class="rmel-iconfont rmel-icon-list-ordered modal-icon"></i>
                    </span>{" "}
                    - Ordered List
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`Ordered lists are a list of items in which each item begins with a number, going from 1 to however many items are in the list. To create an ordered list, add a number followed by a period and a space, then write your text. The numbers don’t have to be in numerical order, but the list should start with the number one. Every item in the list should have a number`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Examples
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  1. First item
                                </Typography>
                                <Typography variant="body1">
                                  2. Second item
                                </Typography>
                                <Typography variant="body1">
                                  3. Third item
                                </Typography>
                                <Typography variant="body1">
                                  4. Fourth item
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  1. First item
                                </Typography>
                                <Typography variant="body1">
                                  2. Second item
                                </Typography>
                                <Typography variant="body1">
                                  3. Third item
                                </Typography>
                                <Typography variant="body1">
                                  4. Fourth item
                                </Typography>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  1. First item
                                </Typography>
                                <Typography variant="body1">
                                  8. Second item
                                </Typography>
                                <Typography variant="body1">
                                  4. Third item
                                </Typography>
                                <Typography variant="body1">
                                  6. Fourth item
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  1. First item
                                </Typography>
                                <Typography variant="body1">
                                  2. Second item
                                </Typography>
                                <Typography variant="body1">
                                  3. Third item
                                </Typography>
                                <Typography variant="body1">
                                  4. Fourth item
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a new line before the beginning of your
                          list and after the end of your list.
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Line Break */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-wrap modal-span"
                      title="Line Break"
                    >
                      <i class="rmel-iconfont rmel-icon-wrap modal-icon"></i>
                    </span>{" "}
                    - Line Break
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`The line break button will end the current line of text and creates a horizontal line underneath it to separate sections. To create a line break, add three or more hyphens (---) on a line by themselves.`}
                  </Typography>
                </Grid>
                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Example
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Input</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">Output</Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">
                                  This text is broken up by a line.
                                </Typography>

                                <br />

                                <Typography variant="body1">---</Typography>

                                <br />
                                <Typography variant="body1">
                                  This text is broken up by a line.
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  This text is broken up by a line.
                                </Typography>

                                <hr />

                                <Typography variant="body1">
                                  This text is broken up by a line.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a new line before and after any line
                          breaks.
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Table */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-table modal-span"
                      title="Table"
                    >
                      <i class="rmel-iconfont rmel-icon-grid modal-icon"></i>
                    </span>{" "}
                    - Table
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To add a table, use three or more hyphens (---) to create each column’s header, and use pipes (|) to separate each column. You should also add a pipe on either end of the row. Cell widths can very, the rendered output will look the same`}
                  </Typography>
                </Grid>

                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Example
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sx={{ pt: 2, pb: 2 }}>
                        <Typography variant="h4" gutterBottom>
                          Input
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                          ## Table Header
                        </Typography>

                        <br />

                        <Typography variant="body1" gutterBottom>
                          | Syntax | Description |
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                          | --- | ----------- |
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                          | Header | Title |
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                          | Paragraph | Text |
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                          Output
                        </Typography>

                        <Typography variant="h2" component="p" gutterBottom>
                          Table Header
                        </Typography>

                        <Table border="1px solid">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="h4">Syntax</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="h4">
                                  Description
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1">Header</Typography>

                                <Typography variant="body1">Title</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  Paragraph{" "}
                                </Typography>

                                <Typography variant="body1">Text </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{ pt: 2 }}
                        >
                          Best Practices
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {`Manually creating tables with hyphens and pipes can be tedious. Be sure to use the included table button to quickly design and create a table with placeholder data and then replace it with your own.`}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Always put a new line before the beginning of your
                          list and after the end of your list.
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Image */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-image modal-span"
                      title="Image"
                    >
                      <i class="rmel-iconfont rmel-icon-image modal-icon"></i>
                    </span>{" "}
                    - Image
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To add an image to your post - hit the image button to create set of square brackets and paranthesis []() . Then, add alternate text that describes the image within the square brackets []. This text will appear if the image fails to load. Then copy and paste your image url into the paranthesis ()`}
                  </Typography>
                </Grid>
                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={6}>
                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography
                              gutterBottom
                              variant="h4"
                              fontWeight="bold"
                            >
                              Example - Working Image Link
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h4" gutterBottom>
                              Input
                            </Typography>

                            <Typography variant gutterBottom="body1">
                              ## Best Coast Pairings Logo
                            </Typography>

                            <Typography
                              variant="body1"
                              gutterBottom
                              sx={{ pb: 2 }}
                            >
                              ![BCP Pirate
                              Logo](https://www.bestcoastpairings.com/images/BannerLogo.png)
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h4" gutterBottom>
                              Output
                            </Typography>
                            <Typography variant="h2" component="p" gutterBottom>
                              Best Coast Pairings Logo
                            </Typography>

                            <img
                              alt="BCP Pirate Logo"
                              src="https://www.bestcoastpairings.com/images/BannerLogo.png"
                              width={"150px"}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography
                              gutterBottom
                              variant="h4"
                              fontWeight="bold"
                            >
                              Example - Broken or Incorrect Image Link
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h4" gutterBottom>
                              Input
                            </Typography>

                            <Typography variant gutterBottom="body1">
                              ## Non-working Best Coast Pairings Logo
                            </Typography>

                            <Typography
                              variant="body1"
                              gutterBottom
                              sx={{ pb: 2 }}
                            >
                              ![Broken BCP Pirate
                              Logo](https://www.bestcoastpairings.com/images/BannerLogo.png)
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h4" gutterBottom>
                              Output
                            </Typography>
                            <Typography variant="h2" component="p" gutterBottom>
                              Non-working Best Coast Pairings Logo
                            </Typography>

                            <img
                              alt="Broken BCP Pirate Logo"
                              src="https://www.bestcoastpairings.com/images/broken-link-image.png"
                              width={"150px"}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1" color="GrayText">
                          {`Note: Only images from an existing website can be used at the moment. To obtain the URL of an image that is already on a website, right click on the image, select "copy image address" then paste this address, as above.`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Link */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-link modal-span"
                      title="Link"
                    >
                      <i class="rmel-iconfont rmel-icon-link modal-icon"></i>
                    </span>{" "}
                    - Link
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`To add a link to your post - copy and paste your link url into the paranthesis () created when hitting the link button. Any text written within the square brackets [] will then become a link.`}
                  </Typography>
                </Grid>
                <Accordion sx={{ width: "100%" }}>
                  <AccordionSummary expandIcon={<BCPAngleDown />}>
                    <Typography variant="body1">More information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="bold">
                          Example
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4">Input</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          sx={{ wordWrap: "break-word" }}
                          variant="body1"
                        >
                          {`Come visit the [Best Coast Pairings](www.bestcoastpairings.com) website to find a local tournament!`}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4">Output</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Come visit the{" "}
                          <Link
                            href="https://www.bestcoastpairings.com"
                            target="_blank"
                          >
                            Best Coast Pairings
                          </Link>{" "}
                          website to find a local tournament!
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {/* Clear */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-clear modal-span"
                      title="Clear"
                    >
                      <i class="rmel-iconfont rmel-icon-delete modal-icon"></i>
                    </span>{" "}
                    - Clear
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`The clear button will completely clear any and all text within the text editor. You will be prompted if you are sure you want to do this. Text deleted this way can be brought back using undo.`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Undo */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-undo modal-span"
                      title="Undo"
                    >
                      <i class="rmel-iconfont rmel-icon-undo modal-icon"></i>
                    </span>{" "}
                    - Undo
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`The undo feature lets you remove or undo single or multiple typing actions, but all actions must be undone in the order they were done – specific actions can't be skipped.`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Redo */}
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {" "}
                    <span
                      class="button button-type-redo modal-span"
                      title="Redo"
                    >
                      <i class="rmel-iconfont rmel-icon-redo modal-icon"></i>
                    </span>{" "}
                    - Redo
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    {`The redo feature lets you repeat or redo single or multiple typing actions, but all actions must be redone in the order they were done – specific actions can't be skipped.`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

export default function MarkupTextEditor(props) {
  const {
    name,
    label,
    error,
    helperText,
    disabled,
    value,
    onChange,
    onBlur,
    sx,
    placeholder,
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });

  const [open, setOpen] = useState(false);

  const screenWidth = window.innerWidth;

  // Used to check what theme the user is using
  const theme = useTheme();
  const styles = { ...sx };

  // Adds help button to editor once it's rendered
  useEffect(() => {
    const helpButtonTest = document.querySelector(".right .button-wrap");

    // Create Button
    const span = document.createElement("span");
    span.className = "button button-type-help";
    span.title = "Help";

    span.addEventListener("click", () => {
      setOpen(true);
    });

    const i = document.createElement("i");
    i.className = "rmel-iconfont rmel-icon-help";

    span.appendChild(i);

    helpButtonTest.prepend(span);
  }, []);

  // Modifies the html generated in the preview window.
  const renderHTML = (text) => {
    const html = mdParser.render(text);

    // Adds target="_blank" to all links in the preview window
    const modifiedHtml = html.replace(/<a\b/g, '<a target="_blank"');

    return modifiedHtml;
  };

  // Sets a default height if none is given
  if (!styles?.height && !styles?.maxHeight && !styles?.minHeight) {
    styles.height = "450px";
  }

  return (
    <Box
      // Because MdEditor by default, does not support a dark mode theme, I have created one below. It detects the user's theme preference and render's accordingly.
      sx={{
        // Full screen preview
        "& .full": {
          top: screenWidth > 600 ? "64px !important" : "56px !important",
          zIndex: "1201 !important",
        },

        // Textarea border
        "& #editorLabel": disabled
          ? {
              color: theme?.palette?.text?.disabled,
            }
          : null,

        // Main text area css

        "& .section-container": disabled
          ? {
              backgroundColor: `${theme?.palette?.background.secondary} !important`,
              color: `${theme?.palette?.text?.disabled} !important`,
            }
          : theme?.palette?.mode === "dark"
            ? {
                backgroundColor: `${theme?.palette?.background.secondary} !important`,
                color: `${theme?.palette?.text?.primary} !important`,
              }
            : null,

        "& .input": {
          fontSize: "16px !important",
        },

        // Textarea border
        "& div":
          theme?.palette?.mode === "dark"
            ? {
                background: `${theme?.palette?.background.tertiary} !important`,
                border: ` 1px solid ${theme?.palette?.background.tertiary} !important`,
                backgroundColor: `${theme?.palette?.background.tertiary} !important`,
                borderRadius: "5px",
              }
            : {
                borderRadius: "5px",
              },

        "& section":
          theme?.palette?.mode === "dark"
            ? {
                background: `${theme?.palette?.background.tertiary} !important`,
                border: `${theme?.palette?.background.tertiary} !important`,
                backgroundColor: `${theme?.palette?.background.tertiary} !important`,
              }
            : null,

        // Button icons
        "& .button": disabled
          ? {
              color: `${theme?.palette?.text?.disabled} !important`,
              pointerEvents: "none !important",
            }
          : theme?.palette?.mode === "dark"
            ? {
                color: `${theme?.palette?.text?.primary} !important`,
              }
            : null,

        "& .rmel-iconfont": {
          fontSize: "22px !important",
        },

        "& .right .button-wrap": { flexWrap: "noWrap !important" },

        "& .button-type-help": {
          backgroundImage: `url("https://friconix.com/png/fi-cnsuxx-question-mark.png") !important`,
          filter:
            theme?.palette?.mode === "dark"
              ? "brightness(8)"
              : "brightness(8) invert(.5)",
          backgroundSize: "75% !important",
          backgroundPosition: "center !important",
          backgroundRepeat: "no-repeat !important",
        },

        "& .button-type-help:hover": {
          filter:
            theme?.palette?.mode === "dark"
              ? "brightness(5)"
              : "brightness(8) invert(1)",
        },

        "& .button-type-code-block": {
          display: "none !important",
        },

        "& .button-type-code-inline": {
          display: "none !important",
        },

        "& .button-type-underline": {
          display: "none !important",
        },

        "& .button-type-quote": {
          display: "none !important",
        },

        // Preview window text
        "& .custom-html-style":
          theme?.palette?.mode === "dark"
            ? {
                color: `${theme?.palette?.text?.primary} !important`,
                backgroundColor: `${theme?.palette?.background.secondary} !important`,
              }
            : null,

        "& .custom-html-style pre, & .custom-html-style code":
          theme?.palette?.mode === "dark"
            ? {
                color: `${theme?.palette?.text?.primary} !important`,
                background: `none repeat scroll 0 0 rgba(102,128,153,.05) !important`,
              }
            : null,

        "& .custom-html-style table, & .custom-html-style th":
          theme?.palette?.mode === "dark"
            ? {
                color: `${theme?.palette?.text?.primary} !important`,
                backgroundColor: `${theme?.palette?.background.tertiary} !important`,
              }
            : null,

        "& .custom-html-style blockquote":
          theme?.palette?.mode === "dark"
            ? {
                color: `${theme?.palette?.text?.primary} !important`,
                borderLeft: `10px solid ${theme?.palette?.background.tertiary}`,
              }
            : null,

        "& .css-1lf0cix div":
          theme?.palette?.mode === "dark"
            ? {
                border: "none",
              }
            : null,

        // Editor border
        "& .rc-md-editor": {
          border: error ? `1px solid #f44336 !important` : "",
        },
      }}
    >
      {label && (
        <Typography id="editorLabel" variant="body1" gutterBottom>
          {label}
        </Typography>
      )}

      <MdEditor
        style={{
          ...styles,
        }}
        placeholder={placeholder ? placeholder : t("yourTextHere")}
        renderHTML={renderHTML}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        name={name}
        readOnly={disabled}
        view={{ menu: true, md: true, html: false }}
        canView={{
          menu: true,
          md: true,
          html: true,
          fullScreen: true,
          hideMenu: true,
        }}
      />

      {open && (
        <HelperModal
          open={open}
          setOpen={setOpen}
          theme={theme}
          screenWidth={screenWidth}
        />
      )}

      {helperText && (
        <Typography
          id="helperText"
          variant="body1"
          color="#f44336"
          gutterBottom
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
